import React from "react";
import { Link } from "react-router-dom";
import { HashLoader } from "react-spinners";
import "./Timetable.css";
import { MainContext } from "../contexts/MainContext";
import { apiRequest } from "../functions/requestMulti";
import { getAgeStr } from "../functions/utils";
import { weekDays } from "../services/constants";
import Tag from "./Tag";
import {
  PositionedSlot,
  TimetableData,
  addDaysIso,
  branchColor,
  clubRooms,
  isoWeekNumber,
  layoutDays,
  locationKey,
  locationName,
  mondayOfIso,
  shortDate,
  tint,
  toIsoDate,
  visibleTimeRange,
  weekDayOfIso,
} from "../functions/timetable";

// Vertical scale of the grid: 67.2px per hour.
const PX_PER_MIN = 1.12;

// Same breakpoint as the CSS that shows one day at a time.
const PHONE_QUERY = "(max-width: 840px)";

/**
 * - "week": a calendar week, Monday to Sunday (wider screens)
 * - "days": seven days from a start day, by default today - on phones people
 *   want what is coming, not the past days of the current week
 */
type RangeMode = "week" | "days";

/**
 * Everything needed to show the timetable again exactly as it was left. The
 * course page keeps it across a visit to a course's detail page (and across
 * switching to the list and back), so returning does not reload anything.
 */
export type TimetableSnapshot = {
  mode: RangeMode;
  start: string;
  locationFilter: string | null;
  // the day shown on narrow screens
  selectedDay: string | null;
  data: TimetableData | null;
  // horizontal scroll of the grid, i.e. which days were in view
  scrollLeft: number;
};

type Props = {
  // "club": all branches of the club, coloured by branch, filterable by room
  scope: "branch" | "club";
  clubId: string;
  branchId?: string;
  initialState?: TimetableSnapshot | null;
};

type State = {
  mode: RangeMode;
  start: string;
  locationFilter: string | null;
  selectedDay: string | null;
  data: TimetableData | null;
  loading: boolean;
  errorMsg: string | null;
};

const today = () => toIsoDate(new Date());

const isPhone = () =>
  typeof window !== "undefined" && !!window.matchMedia
    ? window.matchMedia(PHONE_QUERY).matches
    : false;

/** The first shown day for a mode, so that `day` is in view. */
const startFor = (mode: RangeMode, day: string) =>
  mode === "week" ? mondayOfIso(day) : day;

/**
 * Timetable ("Stundenplan") of a branch or a club: per day one column per
 * room, courses placed by time, so room occupancy can be read off directly.
 */
export default class Timetable extends React.Component<Props, State> {
  static contextType = MainContext;
  private requestCounter = 0;
  private mounted = false;
  private scrollRef = React.createRef<HTMLDivElement>();
  private dateInputRef = React.createRef<HTMLInputElement>();
  private phoneQuery: MediaQueryList | null = null;
  // Set when the user picked a day; the next render scrolls it into view.
  private scrollToSelectedDay = false;

  constructor(props: Props) {
    super(props);
    const mode: RangeMode = isPhone() ? "days" : "week";
    const initial = props.initialState;
    // A snapshot is shown as is only if it holds the data of its own range in
    // the current mode (the phone may have been rotated in the meantime).
    const restored =
      initial &&
      initial.mode === mode &&
      initial.data &&
      initial.data.start === initial.start
        ? initial
        : null;
    const anchorDay = (initial && initial.selectedDay) || today();
    this.state = {
      mode,
      start: restored ? restored.start : startFor(mode, anchorDay),
      locationFilter: initial ? initial.locationFilter : null,
      selectedDay: restored
        ? restored.selectedDay
        : initial
        ? initial.selectedDay
        : null,
      data: restored ? restored.data : null,
      loading: !restored,
      errorMsg: null,
    };
  }

  componentDidMount = () => {
    this.mounted = true;
    if (window.matchMedia) {
      this.phoneQuery = window.matchMedia(PHONE_QUERY);
      if (this.phoneQuery.addEventListener) {
        this.phoneQuery.addEventListener("change", this.handleScreenChange);
      } else {
        this.phoneQuery.addListener(this.handleScreenChange);
      }
    }
    if (this.state.data) {
      // Back from a course: the saved range is already on screen. Put the
      // grid back on the same days, then refresh quietly - free places may
      // have changed, e.g. because the user just booked.
      if (this.scrollRef.current && this.props.initialState) {
        this.scrollRef.current.scrollLeft = this.props.initialState.scrollLeft;
      }
      this.requestTimetable(this.state.start, true);
    } else {
      this.requestTimetable(this.state.start, false, this.state.selectedDay);
    }
  };

  componentDidUpdate = () => {
    if (!this.scrollToSelectedDay || this.state.loading) return;
    this.scrollToSelectedDay = false;
    // Only matters when the grid is wider than the screen (desktop with many
    // rooms); on phones only the selected day is rendered anyway.
    const container = this.scrollRef.current;
    const day = container?.querySelector<HTMLElement>(
      `[data-date="${this.state.selectedDay}"]`
    );
    if (container && day) container.scrollLeft = day.offsetLeft;
  };

  componentWillUnmount = () => {
    this.mounted = false;
    if (this.phoneQuery) {
      if (this.phoneQuery.removeEventListener) {
        this.phoneQuery.removeEventListener("change", this.handleScreenChange);
      } else {
        this.phoneQuery.removeListener(this.handleScreenChange);
      }
    }
  };

  /** Read by the course page right before this component unmounts. */
  getSnapshot = (): TimetableSnapshot => ({
    mode: this.state.mode,
    start: this.state.start,
    locationFilter: this.state.locationFilter,
    selectedDay: this.state.selectedDay,
    data: this.state.data,
    scrollLeft: this.scrollRef.current ? this.scrollRef.current.scrollLeft : 0,
  });

  /** Rotating a tablet or resizing a window switches week <-> next 7 days. */
  handleScreenChange = () => {
    const mode: RangeMode = isPhone() ? "days" : "week";
    if (mode === this.state.mode) return;
    const day = this.state.selectedDay || today();
    const start = startFor(mode, day);
    this.setState({ mode, start, selectedDay: day });
    this.requestTimetable(start, false, day, mode);
  };

  /**
   * @param start first day of the range to load
   * @param silent refresh what is on screen: no spinner, keep the selected
   * day, and keep the old data if the request fails
   * @param dayToShow the day to select once loaded, e.g. the picked one
   * @param mode the range mode, when it is changing in the same update
   */
  requestTimetable = async (
    start: string,
    silent = false,
    dayToShow: string | null = null,
    mode: RangeMode = this.state.mode
  ) => {
    // Fast clicking through weeks: only the last request may write state.
    const requestId = ++this.requestCounter;
    if (!silent) this.setState({ loading: true, errorMsg: null });
    const range = mode === "week" ? { weekStart: start } : { from: start };
    try {
      const { data } =
        this.props.scope === "club"
          ? await apiRequest("api/timetable/club", "GET", {
              clubId: this.props.clubId,
              ...range,
            })
          : await apiRequest("api/timetable/branch", "GET", {
              clubId: this.props.clubId,
              branchId: this.props.branchId,
              ...range,
            });
      if (!this.mounted || requestId !== this.requestCounter) return;
      if (data.success) {
        const days: Array<string> = data.data.days;
        const keepDay =
          silent &&
          this.state.selectedDay !== null &&
          days.includes(this.state.selectedDay);
        let selectedDay = this.defaultDay(data.data, mode);
        if (dayToShow && days.includes(dayToShow)) {
          selectedDay = dayToShow;
        } else if (keepDay) {
          selectedDay = this.state.selectedDay as string;
        }
        this.setState({ data: data.data, loading: false, selectedDay });
      } else if (!silent) {
        this.setState({ loading: false, data: null, errorMsg: data.errorMsgDe });
      }
    } catch (err) {
      console.error(err);
      if (silent || !this.mounted || requestId !== this.requestCounter) return;
      this.setState({
        loading: false,
        data: null,
        errorMsg: "Der Stundenplan konnte nicht geladen werden.",
      });
    }
  };

  /**
   * Week: today when it is in the week, else the first day with courses.
   * Next 7 days: the first day, normally today.
   */
  defaultDay = (data: TimetableData, mode: RangeMode) => {
    if (mode === "days") return data.days[0];
    if (data.days.includes(today())) return today();
    const first = data.slots[0];
    return first ? first.date : data.days[0];
  };

  /** Selects `day`, loading the range that starts at `start` if needed. */
  goTo = (start: string, day: string) => {
    this.scrollToSelectedDay = true;
    if (start === this.state.start) {
      this.setState({ selectedDay: day });
      return;
    }
    this.setState({ start, selectedDay: day });
    this.requestTimetable(start, false, day);
  };

  /** ‹ › move by 7 days and keep the weekday (Wednesday -> next Wednesday). */
  handleShift = (direction: number) => {
    const day = addDaysIso(
      this.state.selectedDay || this.state.start,
      direction * 7
    );
    this.goTo(
      this.state.mode === "week"
        ? mondayOfIso(day)
        : addDaysIso(this.state.start, direction * 7),
      day
    );
  };

  /**
   * Opens the browser's own date picker - on phones that is the familiar
   * system calendar. The input sits invisibly over the range label, so the
   * picker pops up right there.
   */
  handleOpenDatePicker = () => {
    const input: any = this.dateInputRef.current;
    if (!input) return;
    try {
      input.showPicker();
    } catch (err) {
      // Browsers without showPicker() open the picker on focus/click.
      input.focus();
      input.click();
    }
  };

  /**
   * Week: the picked day's week, with that day selected. Next 7 days: the
   * picked day if it is already shown, else seven days starting with it.
   */
  handleDatePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const day = e.target.value;
    if (!day) return;
    if (this.state.mode === "week") {
      this.goTo(mondayOfIso(day), day);
      return;
    }
    const end = addDaysIso(this.state.start, 6);
    const inRange = day >= this.state.start && day <= end;
    this.goTo(inRange ? this.state.start : day, day);
  };

  renderHeader() {
    const { mode, start } = this.state;
    const end = addDaysIso(start, 6);
    const range = `${shortDate(start)} – ${shortDate(end)}${end.slice(0, 4)}`;
    let heading = `KW ${isoWeekNumber(start)}`;
    if (mode === "days") {
      heading =
        start === today()
          ? "Ab heute"
          : `Ab ${weekDays[weekDayOfIso(start)].name.slice(0, 2)} ${shortDate(
              start
            )}`;
    }
    const step = mode === "week" ? "Woche" : "7 Tage";
    const selected = this.state.selectedDay;
    return (
      <div className="timetable-header">
        <button
          className="timetable-nav-btn"
          onClick={() => this.handleShift(-1)}
          aria-label={`Vorherige ${step}`}
        >
          ‹
        </button>
        <div className="timetable-week-picker">
          <button
            type="button"
            className="timetable-week-label"
            onClick={this.handleOpenDatePicker}
            title="Datum wählen"
            aria-label={`${heading}, ${range}. Datum wählen`}
          >
            <span className="timetable-week-number">{heading}</span>
            <span className="timetable-week-range">{range}</span>
          </button>
          <input
            ref={this.dateInputRef}
            type="date"
            className="timetable-date-input"
            // opens on the selected day, while a new range loads on its start
            value={selected && selected >= start && selected <= end ? selected : start}
            onChange={this.handleDatePicked}
            tabIndex={-1}
            aria-hidden="true"
          />
        </div>
        <button
          className="timetable-nav-btn"
          onClick={() => this.handleShift(1)}
          aria-label={`Nächste ${step}`}
        >
          ›
        </button>
      </div>
    );
  }

  /**
   * Club timetable only: one chip per club room (Verein › Örtlichkeiten), so a
   * shared room's occupancy across all branches can be read off. Rooms a
   * single branch created are not offered. The branch timetable has no filter.
   */
  renderLocationFilter(data: TimetableData) {
    if (this.props.scope !== "club") return null;
    const rooms = clubRooms(data.locations, this.context.club?.locations);
    if (rooms.length === 0) return null;
    return (
      <div className="timetable-filter">
        <Tag
          item="Alle Orte"
          selected={this.state.locationFilter === null}
          color={"#5DADE2"}
          onClick={() => this.setState({ locationFilter: null })}
        />
        {rooms.map((location) => (
          <Tag
            key={locationKey(location)}
            item={locationName(location)}
            selected={this.state.locationFilter === locationKey(location)}
            color={"#5DADE2"}
            onClick={() =>
              this.setState({ locationFilter: locationKey(location) })
            }
          />
        ))}
      </div>
    );
  }

  renderSlot(data: TimetableData, slot: PositionedSlot, startMin: number) {
    const ageStr = getAgeStr(slot.minAge, slot.maxAge);
    const timeStr = `${slot.startTime}–${slot.endTime}`;
    const placesStr = slot.full ? "Ausgebucht" : `${slot.freePlaces} frei`;
    const height = Math.max((slot.endMin - slot.startMin) * PX_PER_MIN, 24);
    const color = branchColor(data, slot.branchId);
    const branch = data.branches.find((b) => b.id === slot.branchId);
    const link =
      this.props.scope === "club"
        ? `/club/${this.props.clubId}/branchCourse/${slot.branchId}/${slot.courseId}`
        : `/club/${this.props.clubId}/branch/${slot.branchId}/course/${slot.courseId}`;
    return (
      <div
        key={`${slot.courseId}-${slot.date}-${slot.startTime}`}
        className="timetable-slot"
        style={{
          top: (slot.startMin - startMin) * PX_PER_MIN,
          height,
          left: `${(slot.lane / slot.lanes) * 100}%`,
          width: `${100 / slot.lanes}%`,
        }}
      >
        <Link
          to={link}
          className={`timetable-slot-inner no-underline${
            slot.full ? " timetable-slot-full" : ""
          }`}
          style={{ borderLeftColor: color, backgroundColor: tint(color, 0.16) }}
          title={[
            slot.title,
            slot.sessionTitle,
            this.props.scope === "club" && branch ? branch.name : "",
            timeStr,
            ageStr ? `${ageStr} Jahre` : "",
            placesStr,
          ]
            .filter(Boolean)
            .join("\n")}
        >
          <div className="timetable-slot-title">{slot.title}</div>
          {slot.sessionTitle && (
            <div className="timetable-slot-line">{slot.sessionTitle}</div>
          )}
          <div className="timetable-slot-line">{timeStr}</div>
          {ageStr && <div className="timetable-slot-line">{ageStr} J.</div>}
          <div className="timetable-slot-line timetable-slot-places">
            {placesStr}
          </div>
        </Link>
      </div>
    );
  }

  renderGrid(data: TimetableData) {
    const days = layoutDays(data, {
      locationFilter: this.state.locationFilter,
      allDays: this.state.mode === "days",
    });
    const visibleSlots: Array<PositionedSlot> = [];
    days.forEach((day) =>
      day.columns.forEach((column) => visibleSlots.push(...column.slots))
    );
    if (visibleSlots.length === 0) {
      return (
        <div className="timetable-empty">
          {this.state.mode === "week"
            ? "In dieser Woche finden keine Kurse statt."
            : "In diesen 7 Tagen finden keine Kurse statt."}
        </div>
      );
    }

    // The selected day may not be drawn (a weekend without courses): show the
    // next drawn day instead, or the last one, so a phone never shows nothing.
    const drawn = days.map((day) => day.date);
    const wanted = this.state.selectedDay;
    let selectedDay = drawn[0];
    if (wanted) {
      selectedDay = drawn.includes(wanted)
        ? wanted
        : drawn.find((date) => date > wanted) || drawn[drawn.length - 1];
    }
    const { startMin, endMin } = visibleTimeRange(
      days,
      selectedDay,
      this.state.mode === "days"
    );
    const bodyHeight = (endMin - startMin) * PX_PER_MIN;
    const hours: Array<number> = [];
    for (let min = startMin; min <= endMin; min += 60) hours.push(min);

    return (
      <>
        <div className="timetable-day-select">
          {days.map((day) => (
            <Tag
              key={day.date}
              item={
                day.date === today()
                  ? "Heute"
                  : `${weekDays[day.weekDay].name.slice(0, 2)} ${shortDate(
                      day.date
                    )}`
              }
              selected={day.date === selectedDay}
              color={"#c31924"}
              onClick={() => this.setState({ selectedDay: day.date })}
            />
          ))}
        </div>
        <div className="timetable-scroll" ref={this.scrollRef}>
          <div className="timetable-grid">
            <div className="timetable-axis" style={{ height: bodyHeight }}>
              {hours.map((min) => (
                <div
                  key={min}
                  className="timetable-axis-label"
                  style={{ top: (min - startMin) * PX_PER_MIN }}
                >
                  {`${("0" + min / 60).slice(-2)}:00`}
                </div>
              ))}
            </div>
            {days.map((day) => (
              <div
                key={day.date}
                data-date={day.date}
                className={`timetable-day${
                  day.date === selectedDay ? "" : " timetable-day-collapsed"
                }`}
                // a day is as wide as its rooms, so every room gets the same width
                style={{ ["--timetable-day-rooms" as any]: day.columns.length }}
              >
                <div className="timetable-day-header">
                  {weekDays[day.weekDay].name}{" "}
                  <span className="timetable-day-date">
                    {shortDate(day.date)}
                  </span>
                </div>
                <div className="timetable-day-columns">
                  {day.columns.map((column) => (
                    <div
                      key={column.location ? locationKey(column.location) : "empty"}
                      className="timetable-column"
                    >
                      <div
                        className="timetable-column-header"
                        title={column.location ? locationName(column.location) : ""}
                      >
                        {column.location ? locationName(column.location) : ""}
                      </div>
                      <div
                        className="timetable-column-body"
                        style={{
                          height: bodyHeight,
                          backgroundSize: `100% ${60 * PX_PER_MIN}px`,
                        }}
                      >
                        {column.location === null && (
                          <div className="timetable-day-empty">Keine Kurse</div>
                        )}
                        {column.slots.map((slot) =>
                          this.renderSlot(data, slot, startMin)
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  render() {
    const data = this.state.data;
    return (
      <div className="timetable-container">
        {this.renderHeader()}
        {data && this.renderLocationFilter(data)}
        {this.state.loading ? (
          <div className="timetable-loading">
            <HashLoader color={"#c31924"} size={60} loading={true} />
          </div>
        ) : this.state.errorMsg ? (
          <div className="timetable-error">{this.state.errorMsg}</div>
        ) : (
          data && this.renderGrid(data)
        )}
      </div>
    );
  }
}
